import React, {useContext, useEffect, useRef} from 'react'

import PropTypes from 'prop-types'

import Badge, {atomBadgeDesigns, atomBadgeSizes, atomBadgeTypes} from '@s-ui/react-atom-badge'
import Button, {atomButtonDesigns, atomButtonShapes} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import {useOnScreen} from '@s-ui/react-hooks'
import CardDealerService from '@adv-mt/mt-card-dealer-service'
import PanelBasic from '@adv-mt/mt-panel-basic'
import IconCheck from '@adv-mt/mt-svg-icons/lib/Check'
import TitleBasic, {titleBasicSizes, titleBasicTags} from '@adv-mt/mt-title-basic'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import {ARRAY_OFFSET, BASE_CLASS, CONTEXTS as sectionServicesContext, TRACKS} from './config.js'

const MAX_NUMBER_OF_SERVICES_TO_DISPLAY = 6

function SectionServicesContentRenting({
  isMainServicesIncluded = false,
  context = sectionServicesContext.DETAIL,
  onButtonClick = () => {},
  onServiceClick = () => {},
  services = []
}) {
  const ref = useRef()
  const {i18n} = useContext(Context)

  const {track} = useAnalyticsTrack()
  const [isIntersecting] = useOnScreen({ref, once: true})
  const servicesItem = services.length

  const isSubscriptionDetailsContext = context === sectionServicesContext.DETAIL_SUBSCRIPTION

  const classServicesContent = `${BASE_CLASS}-content ${BASE_CLASS}-content--list`

  const classServiceContentItem = `${BASE_CLASS}-service`

  const textSectionTitle = i18n.t('DEALER_SERVICE.DEALER_SERVICES_SUBSCRIPTION_TITLE')

  const textSectionButton = i18n.t('COMMON.KNOW_MORE')

  const includedServicesList = services
    .map((service, index) => {
      if (!service.isIncluded) return null
      const currentItem = index + ARRAY_OFFSET
      const isLastItem = servicesItem === currentItem
      const refProp = {
        ...(isLastItem && {ref})
      }

      return (
        <div className={classServiceContentItem} key={service.type} onClick={onServiceClick} {...refProp}>
          <CardDealerService
            id={service.type}
            type="compact"
            service={service.title}
            description={service.description}
            isIncluded={service.isIncluded}
          />
        </div>
      )
    })
    .filter(Boolean)

  const hasServicesIncluded = includedServicesList.length > 0

  useEffect(() => {
    hasServicesIncluded && isIntersecting && track(TRACKS.SERVICES_SEEN)
  }, [isIntersecting, hasServicesIncluded, track])

  if (!hasServicesIncluded) {
    return null
  }

  const cutIndex =
    services.length > MAX_NUMBER_OF_SERVICES_TO_DISPLAY ? MAX_NUMBER_OF_SERVICES_TO_DISPLAY : services.length
  const showMoreServicesIncludedButton = services.length > MAX_NUMBER_OF_SERVICES_TO_DISPLAY

  return (
    <>
      <div className={`${BASE_CLASS}-title`}>
        <TitleBasic as={titleBasicTags.h2} title={textSectionTitle} size={titleBasicSizes.xs} />
        {!isSubscriptionDetailsContext && (
          <Button link onClick={onButtonClick} design={atomButtonDesigns.LINK} role="button">
            {textSectionButton}
          </Button>
        )}
      </div>

      <PanelBasic>
        <div className={`${BASE_CLASS}Renting`}>
          {isMainServicesIncluded && (
            <div className={`${BASE_CLASS}Renting--header`}>
              <Badge
                design={atomBadgeDesigns.SOLID}
                icon={<IconCheck />}
                label={i18n.t('DEALER_SERVICE.MAIN_SERVICES_INCLUDED')}
                size={atomBadgeSizes.LARGE}
                type={atomBadgeTypes.SUCCESS}
              />
            </div>
          )}
          <div className={classServicesContent}>{includedServicesList.slice(0, cutIndex)}</div>
          {showMoreServicesIncludedButton && (
            <div className={`${BASE_CLASS}Renting--footer`}>
              <Button
                design={atomButtonDesigns.OUTLINE}
                fullWidth
                shape={atomButtonShapes.CIRCULAR}
                onClick={onButtonClick}
              >
                {i18n.t('DEALER_SERVICE.SHOW_ALL_SERVICES_INCLUDED')}
              </Button>
            </div>
          )}
        </div>
      </PanelBasic>
    </>
  )
}

SectionServicesContentRenting.displayName = 'SectionServicesContentRenting'
SectionServicesContentRenting.propTypes = {
  isMainServicesIncluded: PropTypes.bool,
  context: PropTypes.string,
  onButtonClick: PropTypes.func,
  onServiceClick: PropTypes.func,
  services: PropTypes.array.isRequired
}

export default React.memo(SectionServicesContentRenting)
